/**
 * Mobile player — the QR-launched, phone-playable view of Flow beatmaps.
 * Standalone (no editor chrome). Screens: song menu → difficulty → gameplay.
 *
 * NOTE: the 3D highway gameplay engine lands in a follow-up commit; for now the
 * 'play' screen is a placeholder so the menu → difficulty flow is usable.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { listSongs, loadSong, PlayerSong, PlayerSongEntry, PlayerDifficulty } from './playerApi';
import PlayGame from './PlayGame';
import { color } from '../components/ui/theme';

type Screen = 'menu' | 'difficulty' | 'play';

const DIFF_COLOR: Record<string, string> = {
  Easy: '#22C55E', Medium: '#EAB308', Hard: '#F97316', Expert: '#EF4444',
};

export default function MobilePlayer() {
  const [screen, setScreen] = useState<Screen>('menu');
  const [songs, setSongs] = useState<PlayerSongEntry[]>([]);
  const [song, setSong] = useState<PlayerSong | null>(null);
  const [diff, setDiff] = useState<PlayerDifficulty | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the song list on mount.
  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    listSongs()
      .then(s => { if (alive) setSongs(s); })
      .catch(e => { if (alive) setError(String(e.message || e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const pickSong = useCallback(async (entry: PlayerSongEntry) => {
    setLoading(true); setError(null);
    try {
      const s = await loadSong(entry);
      setSong(s);
      setScreen('difficulty');
    } catch (e: any) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <div style={root}>
      <div style={topbar}>
        {screen !== 'menu' && (
          <button style={backBtn} onClick={() => {
            if (screen === 'play') setScreen('difficulty');
            else { setSong(null); setDiff(null); setScreen('menu'); }
          }}>‹ Back</button>
        )}
        <div style={brand}>SYNAPSE · Play</div>
      </div>

      {error && <div style={errorBox}>{error}<br /><span style={{ opacity: 0.7, fontSize: 12 }}>
        Could not load the bundled songs. Try reloading the page.</span></div>}

      {screen === 'menu' && (
        <div style={list}>
          <div style={h1}>Choose a song</div>
          {loading && <div style={muted}>Loading…</div>}
          {!loading && songs.length === 0 && !error && <div style={muted}>No songs found.</div>}
          {songs.map(s => (
            <button key={s.file} style={card} onClick={() => pickSong(s)}>
              <div style={cardTitle}>{s.name}</div>
              <div style={cardSub}>{s.author}</div>
            </button>
          ))}
        </div>
      )}

      {screen === 'difficulty' && song && (
        <div style={list}>
          <div style={h1}>{song.name}</div>
          <div style={muted}>{song.author} · {Math.round(song.bpm)} BPM</div>
          <div style={{ ...muted, marginTop: 18, marginBottom: 6 }}>Difficulty</div>
          {song.difficulties.map(d => {
            const empty = d.noteCount === 0;
            return (
              <button key={d.name} disabled={empty}
                style={{ ...card, opacity: empty ? 0.4 : 1, cursor: empty ? 'default' : 'pointer',
                         borderColor: (DIFF_COLOR[d.name] || '#888') + (empty ? '33' : '88') }}
                onClick={() => { if (!empty) { setDiff(d); setScreen('play'); } }}>
                <div style={{ ...cardTitle, color: DIFF_COLOR[d.name] || '#fff' }}>{d.name}</div>
                <div style={cardSub}>{empty ? 'empty — not mapped yet' : `${d.noteCount} notes`}</div>
              </button>
            );
          })}
        </div>
      )}

      {screen === 'play' && song && diff && (
        <PlayGame
          audioUrl={song.audioUrl || ''}
          title={song.name}
          rawChart={song.raw}
          diff={(song.raw.difficulties || []).find((d: any) => d.name === diff.name)}
          bpm={song.bpm}
          onExit={() => setScreen('difficulty')}
        />
      )}
    </div>
  );
}


// ── styles ───────────────────────────────────────────────────────────────────
const root: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'linear-gradient(160deg,#0a0a14,#150a22)',
  color: color.ink, fontFamily: "'Montserrat', system-ui, sans-serif",
  display: 'flex', flexDirection: 'column', overflow: 'hidden', touchAction: 'manipulation',
};
const topbar: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
  borderBottom: '1px solid rgba(167,139,250,0.18)',
};
const brand: React.CSSProperties = { fontWeight: 700, letterSpacing: 1, color: '#C4B5FD', fontSize: 14 };
const backBtn: React.CSSProperties = {
  background: 'rgba(124,58,237,0.18)', border: '1px solid rgba(167,139,250,0.4)',
  color: '#E9D5FF', borderRadius: 10, padding: '8px 14px', fontSize: 15, cursor: 'pointer',
};
const list: React.CSSProperties = { flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 };
const h1: React.CSSProperties = { fontSize: 22, fontWeight: 800, margin: '4px 0 2px' };
const muted: React.CSSProperties = { color: '#9B8CFF', fontSize: 14 };
const card: React.CSSProperties = {
  textAlign: 'left', background: 'rgba(20,16,40,0.7)', border: '1px solid rgba(120,80,220,0.3)',
  borderRadius: 14, padding: '16px 18px', cursor: 'pointer', color: color.ink, width: '100%',
};
const cardTitle: React.CSSProperties = { fontSize: 17, fontWeight: 700 };
const cardSub: React.CSSProperties = { fontSize: 13, color: '#9B8CFF', marginTop: 4 };
const errorBox: React.CSSProperties = {
  margin: 16, padding: 14, borderRadius: 12, background: 'rgba(239,68,68,0.12)',
  border: '1px solid rgba(239,68,68,0.4)', color: '#FCA5A5', fontSize: 14,
};
